// productionMilestonesPage.js - Production Milestones page

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import Modal from "./components/Modal";
import {
  CompletionVariance,
  WeeklySqftCompleted,
  FirstPassRate,
  DailyModulesCompleted,
  YtdModuleCompletion,
  EditGoals,
} from "./components/modalContent";
import {
  DEFAULT_GOALS,
  MONTH_NAMES,
  MONTH_NAMES_SHORT,
} from "./mockData/mockProductionMilestonesData";
import useProductionMilestones from "./hooks/useProductionMilestones";
import useWeeklyRolls from "./hooks/useWeeklyRolls";
import useMonthlyTrend from "./hooks/useMonthlyTrend";
import "./productionMilestones.css";
import "../animation.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Dynamic year range: 2025 → current year + 1 future year (generated programmatically,
// never hardcoded). Re-evaluates whenever the module loads so deployments always
// reflect the real calendar year without any manual edits.
const _startYear = 2025;
const _endYear = Math.max(new Date().getFullYear() + 1, _startYear + 1);
const AVAILABLE_YEARS = Array.from(
  { length: _endYear - _startYear + 1 },
  (_, i) => _startYear + i,
);
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const ROW_DEFS = [
  { key: "projRolls", label: "Projected Rolls" },
  { key: "actRolls", label: "Actual Rolls" },
  { key: "projWraps", label: "Projected Wraps" },
  { key: "actWraps", label: "Actual Wraps" },
];

/**
 * Update 5: Algorithmic business week computation - fully independent of WEEKS_DATA.
 * Generates keys in the same format as WEEKS_DATA ('YYYY-M-Wn') so the Apply
 * lookup works without any coupling between the UI and the data file.
 */
function computeBusinessWeeks(year, monthIdx) {
  const firstOfMonth = new Date(year, monthIdx, 1);
  const lastOfMonth = new Date(year, monthIdx + 1, 0);

  // Step back to the Monday of the week that contains the first day of the month
  const cursor = new Date(firstOfMonth);
  const dow = cursor.getDay(); // 0=Sun … 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow; // e.g. Wed(3) → -2
  cursor.setDate(cursor.getDate() + offset);

  const fmtD = (d) => {
    const m = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][d.getMonth()];
    return `${m} ${d.getDate()}`;
  };

  const weeks = [];
  let wNum = 1;

  while (cursor <= lastOfMonth) {
    const mon = new Date(cursor);
    const fri = new Date(cursor);
    fri.setDate(fri.getDate() + 4);

    // Include if at least one Mon–Fri day falls in the target month/year
    const hasDay = [0, 1, 2, 3, 4].some((i) => {
      const d = new Date(mon);
      d.setDate(d.getDate() + i);
      return d.getMonth() === monthIdx && d.getFullYear() === year;
    });

    if (hasDay) {
      const spansBoundary =
        mon.getMonth() !== fri.getMonth() ||
        mon.getFullYear() !== fri.getFullYear();

      weeks.push({
        // Key matches WEEKS_DATA format exactly
        key: `${year}-${monthIdx}-W${wNum}`,
        label: `${fmtD(mon)} – ${fmtD(fri)}`,
        spansBoundary,
      });
      wNum++;
    }
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
}

/**
 * Given a date string (YYYY-MM-DD or "YYYY-MM-DD HH:mm"), returns the
 * business-week key that contains that date, in the same format used by
 * WEEKS_DATA and computeBusinessWeeks: "YYYY-monthIdx-Wn".
 *
 * The key's monthIdx is always the month of the week's Monday, matching the
 * WEEKS_DATA convention (e.g. "Apr 28 – May 02" → key month = April = 3).
 *
 * Returns null if the date is invalid.
 */
function getWeekKeyForDate(dateStr) {
  const normalized = dateStr.length === 10 ? dateStr + "T12:00:00" : dateStr;
  const date = new Date(normalized);
  console.debug(
    "[getWeekKeyForDate] input:",
    dateStr,
    "→ normalized:",
    normalized,
    "→ parsed:",
    date.toString(),
  );
  if (isNaN(date)) {
    console.error("[getWeekKeyForDate] ✗ Date parse failed for:", normalized);
    return null;
  }

  // Find the Monday of the week containing this date
  const dow = date.getDay();
  const monday = new Date(date);
  monday.setDate(monday.getDate() + (dow === 0 ? -6 : 1 - dow));
  console.debug(
    "[getWeekKeyForDate] dow:",
    dow,
    "→ Monday:",
    monday.toDateString(),
  );

  // Start with the original date's month/year
  const targetYear = date.getFullYear();
  const targetMonth = date.getMonth();

  console.debug(
    "[getWeekKeyForDate] searching for date in → year:",
    targetYear,
    "monthIdx:",
    targetMonth,
  );

  // Helper function to check if a week contains our target date
  const checkWeeksForDate = (year, monthIdx) => {
    const weeks = computeBusinessWeeks(year, monthIdx);
    const targetTime = date.getTime();

    console.debug(
      `[getWeekKeyForDate] checking ${year}-${monthIdx} → ${weeks.length} weeks`,
    );

    for (const week of weeks) {
      // Parse the week label to get the date range
      // Format: "Mar 17 – Mar 21" or "Apr 28 – May 02"
      const parts = week.label.split(" – ");
      if (parts.length !== 2) continue;

      // Parse start date (e.g., "Mar 17")
      const startParts = parts[0].trim().split(" ");
      const startMonth = MONTH_NAMES_SHORT.indexOf(startParts[0]);
      const startDay = parseInt(startParts[1], 10);
      const startDate = new Date(year, startMonth, startDay, 12, 0, 0);

      // Parse end date (e.g., "Mar 21")
      const endParts = parts[1].trim().split(" ");
      const endMonth = MONTH_NAMES_SHORT.indexOf(endParts[0]);
      const endDay = parseInt(endParts[1], 10);
      // End month might be in next year if it's December → January span
      const endYear =
        endMonth < startMonth && startMonth === 11 ? year + 1 : year;
      const endDate = new Date(endYear, endMonth, endDay, 12, 0, 0);

      console.debug(
        `[getWeekKeyForDate] week ${week.key}: ${week.label} → start:`,
        startDate.toDateString(),
        "end:",
        endDate.toDateString(),
        "target:",
        date.toDateString(),
      );

      // Check if our date falls within this week's range
      if (
        targetTime >= startDate.getTime() &&
        targetTime <= endDate.getTime()
      ) {
        console.debug("[getWeekKeyForDate] ✓ resolved:", week.key);
        return { year, monthIdx, weekKey: week.key };
      }
    }

    return null;
  };

  // First: check the target date's own month
  const result = checkWeeksForDate(targetYear, targetMonth);
  if (result) return result;

  console.debug(
    "[getWeekKeyForDate] not found in target month, checking adjacent months...",
  );

  // Second: check previous month (for dates early in the month)
  const prevMonth = targetMonth - 1 < 0 ? 11 : targetMonth - 1;
  const prevYear = targetMonth - 1 < 0 ? targetYear - 1 : targetYear;
  const prevResult = checkWeeksForDate(prevYear, prevMonth);
  if (prevResult) return prevResult;

  // Third: check next month (for dates late in the month)
  const nextMonth = targetMonth + 1 > 11 ? 0 : targetMonth + 1;
  const nextYear = targetMonth + 1 > 11 ? targetYear + 1 : targetYear;
  const nextResult = checkWeeksForDate(nextYear, nextMonth);
  if (nextResult) return nextResult;

  console.error(
    "[getWeekKeyForDate] ✗ Date not found in any week. date:",
    date.toDateString(),
  );
  return null;
}
/**
 * Expands sparse data points to cover every calendar day of the month.
 * Weekend days and days with no data get sqft/personnel = null so recharts
 * connectNulls={false} breaks the line at gaps → separate work-week segments.
 */
function buildFullMonthData(year, monthIdx, dataPoints) {
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const dataMap = {};
  dataPoints.forEach((d) => {
    dataMap[d.day] = d;
  });

  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dow = new Date(year, monthIdx, day).getDay(); // 0=Sun
    const isWeekend = dow === 0 || dow === 6;
    const point = dataMap[day];
    return {
      day,
      sqft: point ? point.sqft : null,
      personnel: point ? point.personnel : null,
      isWeekend,
    };
  });
}

function cellClass(actual, projected) {
  if (actual > projected) return "over";
  if (actual < projected) return "under";
  return "equal";
}

function fmtSqft(n) {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

// ─── Trend tooltip ─────────────────────────────────────────────────────────────

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  if (d.sqft === null) return null; // no tooltip on gap days
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">Day {label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#cc0000" }}
        />
        {d.sqft.toLocaleString()} sqft completed
      </div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#718096" }}
        />
        {d.personnel} active personnel
      </div>
    </div>
  );
};

// ─── Rolls bar chart tooltip ───────────────────────────────────────────────────

const RollsTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="pml-chart-tooltip-row">
          <div
            className="pml-chart-tooltip-dot"
            style={{ background: p.fill }}
          />
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

// ─── Goals modal ──────────────────────────────────────────────────────────────

const GoalsModalContent = ({ goals, onSave }) => {
  const [form, setForm] = useState({
    ytdCompletion: goals.ytdCompletion,
    weeklySqft: goals.weeklySqft,
    firstPassRate: goals.firstPassRate,
    dailyModulesCompleted: goals.dailyModulesCompleted,
  });
  const [milestones, setMilestones] = useState(
    goals.milestones.map((m) => ({ ...m })),
  );

  const handleFormChange = (key, value) =>
    setForm((p) => ({ ...p, [key]: value }));
  const handleMilestoneChange = (id, field, value) =>
    setMilestones((p) =>
      p.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    );
  const addMilestone = () =>
    setMilestones((p) => [
      ...p,
      { id: Date.now(), label: "", dueDate: "", done: false },
    ]);
  const removeMilestone = (id) =>
    setMilestones((p) => p.filter((m) => m.id !== id));

  return (
    <div className="pml-goals-form">
      <div>
        <p className="pml-goals-section-title">KPI Targets</p>
        <div className="pml-goals-grid">
          {[
            { key: "ytdCompletion", label: "YTD Completion %", step: "0.1" },
            { key: "weeklySqft", label: "Weekly SQFT Target", step: "1" },
            { key: "firstPassRate", label: "First Pass Rate %", step: "0.1" },
            {
              key: "dailyModulesCompleted",
              label: "Daily Modules Completed %",
              step: "0.1",
            },
          ].map(({ key, label, step }) => (
            <div className="pml-form-field" key={key}>
              <label className="pml-form-label">{label}</label>
              <input
                type="number"
                className="pml-form-input"
                value={form[key]}
                step={step}
                onChange={(e) =>
                  handleFormChange(
                    key,
                    step === "1"
                      ? parseInt(e.target.value, 10) || 0
                      : parseFloat(e.target.value) || 0,
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="pml-goals-section-title">Production Milestones</p>
        <div className="pml-milestones-list">
          {milestones.map((m) => (
            <div key={m.id} className="pml-milestone-row">
              <input
                type="text"
                className="pml-milestone-input"
                value={m.label}
                placeholder="Milestone description"
                onChange={(e) =>
                  handleMilestoneChange(m.id, "label", e.target.value)
                }
              />
              <input
                type="date"
                className="pml-milestone-date"
                value={m.dueDate}
                onChange={(e) =>
                  handleMilestoneChange(m.id, "dueDate", e.target.value)
                }
              />
              <button
                className="pml-milestone-remove"
                onClick={() => removeMilestone(m.id)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
        <button className="pml-add-milestone-btn" onClick={addMilestone}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Milestone
        </button>
      </div>
      <button
        className="pml-goals-save-btn"
        onClick={() => onSave({ ...form, milestones })}
      >
        Save Goals
      </button>
    </div>
  );
};

// ─── KPI card ─────────────────────────────────────────────────────────────────

// Update 1: ok = UP arrow (over target = good), alert = DOWN arrow (under target = bad)
const VARIANT_ICONS = {
  ok: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="6 16 12 10 18 16" />
    </svg>
  ),
  amber: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  alert: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="18 8 12 14 6 8" />
    </svg>
  ),
};

const KpiCard = ({
  label,
  value,
  sub,
  subVariant,
  hint,
  alert,
  clickable,
  onClick,
}) => (
  <div
    className={[
      "pml-kpi-card",
      clickable ? "pml-kpi-card--clickable" : "",
      alert ? "pml-kpi-card--alert" : "",
      subVariant ? `pml-kpi-card--${subVariant}` : "",
    ]
      .filter(Boolean)
      .join(" ")}
    onClick={clickable ? onClick : undefined}
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : undefined}
    onKeyDown={
      clickable && onClick
        ? (e) => {
            if (e.key === "Enter") onClick();
          }
        : undefined
    }
  >
    <div className="pml-kpi-card-inner">
      <span className="pml-kpi-label">{label}</span>
      <div className="pml-kpi-value-row">
        <span
          className={`pml-kpi-value${subVariant ? ` pml-kpi-value--${subVariant}` : ""}`}
        >
          {value}
        </span>
        {subVariant && (
          <span
            className={`pml-kpi-trend-icon pml-kpi-trend-icon--${subVariant}`}
          >
            {VARIANT_ICONS[subVariant]}
          </span>
        )}
      </div>
      {sub && (
        <span
          className={`pml-kpi-sub${subVariant ? ` pml-kpi-sub--${subVariant}` : ""}`}
        >
          {sub}
        </span>
      )}
      {hint && <span className="pml-kpi-hint">{hint}</span>}
    </div>
    <div className="pml-kpi-accent" />
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ProductionMilestonesPage = () => {
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [activeModal, setActiveModal] = useState(null);
  const location = useLocation();

  // ── Page-load data: 5 KPI cards + default (current-month) trend chart ──────
  // See useProductionMilestones.js for why this is scoped separately from the
  // filtered Weekly Rolls / Monthly Trend hooks below.
  const {
    metrics,
    loading: milestonesLoading,
    error: milestonesError,
  } = useProductionMilestones();

  // Track whether we've already consumed the incoming openModal state so that
  // navigating back/forward with browser history doesn't re-trigger the modal.
  const consumedRef = useRef(false);
  // Ref for the Weekly Mod Rolls card so we can scroll it into view on deep-link.
  const rollsSectionRef = useRef(null);

  const openModal = useCallback((id) => setActiveModal(id), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  // Auto-open a modal when arriving via navigate(..., { state: { openModal } }).
  // We clear the state immediately after consuming it so a page reload or
  // history traversal doesn't re-open the modal unexpectedly.
  useEffect(() => {
    const incoming = location.state?.openModal;
    if (incoming && !consumedRef.current) {
      consumedRef.current = true;
      openModal(incoming);
      // Replace the current history entry without the state payload so
      // subsequent renders/back-navigations don't re-trigger.
      window.history.replaceState(
        {
          ...window.history.state,
          usr: { ...location.state, openModal: null },
        },
        "",
      );
    }
  }, [location.state, openModal]);

  const handleSaveGoals = useCallback(
    (updated) => {
      setGoals(updated);
      closeModal();
    },
    [closeModal],
  );

  // ── Weekly Mod Rolls filter ───────────────────────────────────────
  //
  // When arriving via navigate(..., { state: { rollDate } }) from ModRollTime,
  // we resolve the date → week key SYNCHRONOUSLY here - before the useState
  // calls - so the very first render already has the correct filter values.
  // A useEffect approach sets state after the first render, causing a two-render
  // cycle where the table appears unfiltered on load before snapping to the right
  // week. Initialising from the resolved values eliminates that entirely.
  //
  // _initRoll is a plain variable (not a hook), so it's fine to declare it
  // between hooks as long as it's not inside a conditional. It re-runs on every
  // render but is cheap (O(weeks-in-month)) and React discards the value after
  // the first render for the lazy-initialiser useState calls.
  const _initRoll = (() => {
    const d = location.state?.rollDate;
    console.debug("[_initRoll] location.state:", location.state);
    console.debug("[_initRoll] rollDate from state:", d);
    if (!d) {
      // No incoming rollDate - default to the current business week so the
      // table loads automatically on page load without any user interaction.
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayStr = `${yyyy}-${mm}-${dd}`;
      console.debug(
        "[_initRoll] No rollDate - defaulting to current week:",
        todayStr,
      );
      const todayResult = getWeekKeyForDate(todayStr);
      if (todayResult) {
        const weeks = computeBusinessWeeks(
          todayResult.year,
          todayResult.monthIdx,
        );
        const meta = weeks.find((w) => w.key === todayResult.weekKey) || null;
        console.debug(
          "[_initRoll] ✓ current-week defaults →",
          todayResult.weekKey,
        );
        return { ...todayResult, meta };
      }
      // Absolute fallback: current month/year, no week pre-selected
      console.warn(
        "[_initRoll] Could not resolve current week - using month defaults only",
      );
      return {
        year: yyyy,
        monthIdx: today.getMonth(),
        weekKey: "",
        meta: null,
      };
    }
    const result = getWeekKeyForDate(d);
    console.debug("[_initRoll] getWeekKeyForDate result:", result);
    if (!result) {
      console.error(
        "[_initRoll] ✗ getWeekKeyForDate returned null - filters will use defaults",
      );
      return null;
    }
    const weeks = computeBusinessWeeks(result.year, result.monthIdx);
    const meta = weeks.find((w) => w.key === result.weekKey) || null;
    console.debug(
      "[_initRoll] availableWeeks:",
      weeks.map((w) => w.key),
      "meta found:",
      meta,
    );
    console.debug(
      "[_initRoll] ✓ initialising roll filters →",
      result.weekKey,
      "year:",
      result.year,
      "monthIdx:",
      result.monthIdx,
    );
    return { ...result, meta };
  })();

  const [rollYear, setRollYear] = useState(() => {
    const v = _initRoll?.year ?? new Date().getFullYear();
    console.debug("[useState rollYear] initialised to:", v);
    return v;
  });
  const [rollMonth, setRollMonth] = useState(() => {
    const v = _initRoll?.monthIdx ?? new Date().getMonth();
    console.debug("[useState rollMonth] initialised to:", v);
    return v;
  });
  const [rollWeekKey, setRollWeekKey] = useState(() => {
    const v = _initRoll?.weekKey ?? "";
    console.debug("[useState rollWeekKey] initialised to:", v);
    return v;
  });
  const [appliedRollWeekKey, setAppliedRollWeekKey] = useState(() => {
    const v = _initRoll?.weekKey ?? "";
    console.debug("[useState appliedRollWeekKey] initialised to:", v);
    return v;
  });
  const [appliedWeekMeta, setAppliedWeekMeta] = useState(() => {
    const v = _initRoll?.meta ?? null;
    console.debug("[useState appliedWeekMeta] initialised to:", v);
    return v;
  });

  // One-time mount effect: scroll the rolls card into view when arriving via a
  // rollDate deep-link, and strip the payload from history so back-navigation
  // doesn't re-trigger the initialisation on remount.
  useEffect(() => {
    console.debug(
      "[mountEffect] rollYear:",
      rollYear,
      "rollMonth:",
      rollMonth,
      "rollWeekKey:",
      rollWeekKey,
      "appliedRollWeekKey:",
      appliedRollWeekKey,
    );
    console.debug("[mountEffect] location.state:", location.state);
    if (!location.state?.rollDate) {
      console.debug(
        "[mountEffect] No rollDate in location.state - skipping scroll/cleanup",
      );
      return;
    }

    rollsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    window.history.replaceState(
      {
        ...window.history.state,
        usr: { ...location.state, rollDate: null },
      },
      "",
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Weeks computed purely from date math - no WEEKS_DATA dependency
  const availableWeeks = useMemo(
    () => computeBusinessWeeks(rollYear, rollMonth),
    [rollYear, rollMonth],
  );

  const handleRollMonthChange = (e) => {
    setRollMonth(parseInt(e.target.value, 10));
    setRollWeekKey("");
  };
  const handleRollYearChange = (e) => {
    setRollYear(parseInt(e.target.value, 10));
    setRollWeekKey("");
  };

  // On Apply: commit the selected week key AND snapshot the algorithmically-
  // computed metadata (spansBoundary etc.) so the badge logic never depends on
  // whether the WEEKS_DATA entry manually sets spansBoundary: true.
  const handleApplyRolls = () => {
    if (rollWeekKey) {
      setAppliedRollWeekKey(rollWeekKey);
      const meta = availableWeeks.find((w) => w.key === rollWeekKey) || null;
      setAppliedWeekMeta(meta);
    }
  };

  // ── Live weekly rolls data ──────────────────────────────────────────────────
  // Re-fetches whenever appliedRollWeekKey changes — covers both the normal
  // Apply-button flow and the ModRollTime modal deep-link, since _initRoll
  // (above) has already resolved either path into appliedRollWeekKey's
  // initial value before this hook's first effect run. See useWeeklyRolls.js
  // for the full reasoning.
  const {
    weekLabel: appliedWeekLabel,
    weekStart: appliedWeekStart,
    weekEnd: appliedWeekEnd,
    spansBoundary: appliedWeekSpansBoundary,
    rollLeader: appliedWeekRollLeader,
    days: appliedWeekDays,
    loading: rollsLoading,
    error: rollsError,
    noDataForWeek,
  } = useWeeklyRolls(appliedRollWeekKey);

  // Adapter object matching the shape the JSX below already expects
  // (appliedWeek.label / appliedWeek.days[...]) so the render code didn't
  // need to change beyond this single derivation.
  const appliedWeek = useMemo(() => {
    if (!appliedRollWeekKey || !appliedWeekDays) return null;
    return {
      label: appliedWeekLabel,
      weekStart: appliedWeekStart,
      weekEnd: appliedWeekEnd,
      spansBoundary: appliedWeekSpansBoundary,
      rollLeader: appliedWeekRollLeader,
      days: appliedWeekDays,
    };
  }, [
    appliedRollWeekKey,
    appliedWeekDays,
    appliedWeekLabel,
    appliedWeekStart,
    appliedWeekEnd,
    appliedWeekSpansBoundary,
    appliedWeekRollLeader,
  ]);

  // Update 3: transform week days into bar chart format when a week is loaded
  const rollsChartData = useMemo(() => {
    if (!appliedWeek) return [];
    return DAY_LABELS.map((day) => ({
      day,
      "Proj. Rolls": appliedWeek.days[day].projRolls,
      "Act. Rolls": appliedWeek.days[day].actRolls,
      "Proj. Wraps": appliedWeek.days[day].projWraps,
      "Act. Wraps": appliedWeek.days[day].actWraps,
    }));
  }, [appliedWeek]);

  // ── Monthly Trend filter ─────────────────────────────────────────

  const [trendYear, setTrendYear] = useState(() => new Date().getFullYear());
  const [trendMonth, setTrendMonth] = useState(() => new Date().getMonth());
  const [appliedTrendYear, setAppliedTrendYear] = useState(() =>
    new Date().getFullYear(),
  );
  const [appliedTrendMonth, setAppliedTrendMonth] = useState(() =>
    new Date().getMonth(),
  );

  const handleApplyTrend = () => {
    setAppliedTrendYear(trendYear);
    setAppliedTrendMonth(trendMonth);
  };

  // ── Live monthly trend data ──────────────────────────────────────────────────
  // Re-fetches whenever appliedTrendYear/appliedTrendMonth change (Apply button).
  // `days` stays sparse (business days only) exactly like the old MONTHLY_TREND
  // mock shape, so buildFullMonthData below needs no changes.
  const {
    days: trendDataRaw,
    stats: trendStats,
    loading: trendLoading,
    error: trendError,
  } = useMonthlyTrend(appliedTrendYear, appliedTrendMonth);

  // Update 4: expand to all calendar days; nulls cause line breaks at weekends
  const trendDataFull = useMemo(
    () => buildFullMonthData(appliedTrendYear, appliedTrendMonth, trendDataRaw),
    [appliedTrendYear, appliedTrendMonth, trendDataRaw],
  );

  // ── KPI values ──────────────────────────────────────────────────
  const { ytdModules, variancePct, weeklySqft, firstPassRate, dailyModules } =
    metrics;

  const ytdCompletionAmt = ytdModules.value;
  const ytdCompletionTarget = ytdModules.target;
  const completionVariancePct = variancePct.value;
  const completionVarianceTarget = variancePct.target;
  const weeklySqftCompleted = weeklySqft.value;
  const weeklySqftTarget = weeklySqft.target;
  const firstPassRatePct = firstPassRate.value;
  const firstPassRateTarget = firstPassRate.target;
  const dailyModulesCompletedPct = dailyModules.value;
  const dailyModulesTarget = dailyModules.target;

  // Calculate completion variance based on amounts
  const completionVarianceAmt = ytdCompletionAmt - ytdCompletionTarget;

  const ytdVariant =
    ytdCompletionAmt >= ytdCompletionTarget
      ? "ok"
      : ytdCompletionAmt >= ytdCompletionTarget * 0.94 // ~5% tolerance
        ? "amber"
        : "alert";

  const cvVariant =
    completionVariancePct >= completionVarianceTarget
      ? "ok"
      : completionVariancePct >= completionVarianceTarget - 2
        ? "amber"
        : "alert";

  const sqftVariant =
    weeklySqftCompleted >= weeklySqftTarget
      ? "ok"
      : weeklySqftCompleted >= weeklySqftTarget * 0.9
        ? "amber"
        : "alert";

  const fprVariant =
    firstPassRatePct >= firstPassRateTarget
      ? "ok"
      : firstPassRatePct >= firstPassRateTarget - 3
        ? "amber"
        : "alert";

  const dmVariant =
    dailyModulesCompletedPct >= dailyModulesTarget
      ? "ok"
      : dailyModulesCompletedPct >= dailyModulesTarget - 5
        ? "amber"
        : "alert";

  // ────────────────────────────────────────────────────────────────

  return (
    <div className="pml-page page-enter">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="pml-page-header">
        <div className="pml-page-header-left">
          <h1>Production Milestones</h1>
          <p className="pml-page-subtitle">
            Track KPIs, mod rolls, and monthly production trends across all
            active buildings
          </p>
        </div>
        <button className="pml-goals-btn" onClick={() => openModal("goals")}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
          Update Goals
        </button>
      </div>

      {/* ── Page-load error (KPI cards + default trend) ───────────────── */}
      {milestonesError && (
        <div className="pml-error-banner">
          Failed to load production milestones data: {milestonesError}
        </div>
      )}

      {/* ── 5 KPI cards ────────────────────────────────── */}
      <div className="pml-kpi-row">
        <KpiCard
          label="YTD Module Completion"
          value={milestonesLoading ? "—" : `${ytdCompletionAmt}`}
          sub={`Target: ${ytdCompletionTarget}`}
          subVariant={ytdVariant}
          alert={ytdVariant === "alert"}
          hint="Click for breakdown"
          clickable
          onClick={() => openModal("ytdCompletion")}
        />

        <KpiCard
          label="8 Week Avg On Time Completion"
          value={milestonesLoading ? "—" : `${completionVariancePct}%`}
          sub={`Target: ${completionVarianceTarget}%`}
          subVariant={cvVariant}
          alert={cvVariant === "alert"}
          hint="Click for breakdown"
          clickable
          onClick={() => openModal("completionVariance")}
        />

        <KpiCard
          label="Weekly SQFT Completed"
          value={milestonesLoading ? "—" : fmtSqft(weeklySqftCompleted)}
          sub={`Target: ${fmtSqft(weeklySqftTarget)}`}
          subVariant={sqftVariant}
          alert={sqftVariant === "alert"}
          hint="Click for breakdown"
          clickable
          onClick={() => openModal("weeklySqft")}
        />

        <KpiCard
          label="Monthly First Pass Rate (Utilities)"
          value={milestonesLoading ? "—" : `${firstPassRatePct}%`}
          sub={`Target: ${firstPassRateTarget}%`}
          subVariant={fprVariant}
          alert={fprVariant === "alert"}
          hint="Click for breakdown"
          clickable
          onClick={() => openModal("firstPassRate")}
        />

        <KpiCard
          label="Daily Modules Completed"
          value={milestonesLoading ? "—" : `${dailyModulesCompletedPct}`}
          sub={`Target: ${dailyModulesTarget}`}
          subVariant={dmVariant}
          alert={dmVariant === "alert"}
          hint="Click for breakdown"
          clickable
          onClick={() => openModal("dailyModules")}
        />
      </div>

      {/* ── Data row ───────────────────────────────────── */}
      <div className="pml-data-row">
        {/* Left: Weekly Mod Rolls */}
        <div className="pml-card" ref={rollsSectionRef}>
          <h2 className="pml-section-title">Weekly Mod Rolls</h2>

          {/* Update 2: red left border on focus only - no background fill animation */}
          <div className="pml-filter-row">
            <span className="pml-filter-label">Year</span>
            <select
              className="pml-select"
              value={rollYear}
              onChange={handleRollYearChange}
            >
              {AVAILABLE_YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <span className="pml-filter-label">Month</span>
            <select
              className="pml-select"
              value={rollMonth}
              onChange={handleRollMonthChange}
            >
              {MONTH_NAMES.map((name, idx) => (
                <option key={idx} value={idx}>
                  {name}
                </option>
              ))}
            </select>

            <span className="pml-filter-label">Week</span>
            <select
              className="pml-select"
              value={rollWeekKey}
              onChange={(e) => setRollWeekKey(e.target.value)}
            >
              <option value="">- Select -</option>
              {availableWeeks.map((w) => (
                <option key={w.key} value={w.key}>
                  {w.label}
                  {w.spansBoundary ? " *" : ""}
                </option>
              ))}
            </select>

            <button
              className="pml-apply-btn"
              onClick={handleApplyRolls}
              disabled={!rollWeekKey}
            >
              Apply
            </button>
          </div>

          {rollsLoading ? (
            <div className="pml-empty-state">
              <div className="pml-spinner" aria-hidden="true" />
              <p>Loading roll data…</p>
            </div>
          ) : rollsError ? (
            <div className="pml-empty-state">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p>Couldn't load roll data</p>
              <span>{rollsError}</span>
            </div>
          ) : !appliedRollWeekKey ? (
            <div className="pml-empty-state">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p>Select a year, month, and business week</p>
              <span>Then click Apply to load roll data</span>
            </div>
          ) : noDataForWeek || !appliedWeek ? (
            <div className="pml-empty-state">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {/* Update 5: consistent empty state message */}
              <p>No data found for this timeframe</p>
              <span>Please select a new date range</span>
            </div>
          ) : (
            // Update 3: split layout - table top, bar chart bottom
            <div className="pml-rolls-content">
              <div className="pml-rolls-meta">
                <span className="pml-rolls-range">
                  {appliedWeek.label}
                  {/* Use the algorithmically-computed spansBoundary from appliedWeekMeta
                      so the badge fires for ALL cross-month weeks - not just the ones
                      that happen to have spansBoundary: true set manually in WEEKS_DATA. */}
                  {appliedWeekMeta?.spansBoundary && (
                    <span className="pml-span-badge">Spans Month</span>
                  )}
                </span>
                <span className="pml-rolls-leader">
                  Roll Leader: <span>{appliedWeek.rollLeader}</span>
                </span>
              </div>

              {/* Table - upper portion */}
              <div className="pml-rolls-table-wrap">
                <table className="pml-rolls-table">
                  <thead>
                    <tr>
                      <th></th>
                      {DAY_LABELS.map((d) => (
                        <th key={d}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ROW_DEFS.map((row) => {
                      const isActual =
                        row.key === "actRolls" || row.key === "actWraps";
                      const projKey =
                        row.key === "actRolls" ? "projRolls" : "projWraps";
                      return (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          {DAY_LABELS.map((day) => {
                            const cell = appliedWeek.days[day];
                            const val = cell[row.key];
                            const projVal = isActual ? cell[projKey] : null;
                            const cls = isActual ? cellClass(val, projVal) : "";
                            return (
                              <td
                                key={day}
                                className={cls ? `pml-cell--${cls}` : ""}
                              >
                                {val}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Bar chart - lower portion: projected vs actual by day */}
              <div className="pml-rolls-chart-wrap">
                <p className="pml-rolls-chart-label">
                  Daily Rolls &amp; Wraps - Projected vs Actual
                </p>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={rollsChartData}
                    barGap={2}
                    barCategoryGap="20%"
                    margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: "#718096" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e2e8f0" }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#718096" }}
                      tickLine={false}
                      axisLine={false}
                      width={28}
                    />
                    <Tooltip content={<RollsTooltip />} />
                    // AFTER
                    <Legend
                      content={() => (
                        <div
                          style={{
                            display: "flex",
                            gap: 16,
                            justifyContent: "center",
                            fontSize: 11,
                            paddingTop: 4,
                          }}
                        >
                          {[
                            { label: "Proj. Rolls", color: "#000000" },
                            { label: "Act. Rolls", color: "#cc0000" },
                            { label: "Proj. Wraps", color: "#0066ff" },
                            { label: "Act. Wraps", color: "#38a169" },
                          ].map(({ label, color }) => (
                            <span
                              key={label}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <svg
                                width="10"
                                height="10"
                                style={{ flexShrink: 0 }}
                              >
                                <rect
                                  width="10"
                                  height="10"
                                  rx="2"
                                  fill={color}
                                />
                              </svg>
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                    />
                    <Bar
                      dataKey="Proj. Rolls"
                      fill="#000000"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="Act. Rolls"
                      fill="#cc0000"
                      fillOpacity={0.85}
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="Proj. Wraps"
                      fill="#0066ff"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="Act. Wraps"
                      fill="#38a169"
                      fillOpacity={0.85}
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Right: Monthly Production Trend */}
        <div className="pml-card">
          <h2 className="pml-section-title">Monthly Production Trend</h2>

          {/* Update 2: same simple left-border focus style as rolls */}
          <div className="pml-filter-row">
            <span className="pml-filter-label">Year</span>
            <select
              className="pml-select"
              value={trendYear}
              onChange={(e) => setTrendYear(parseInt(e.target.value, 10))}
            >
              {AVAILABLE_YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <span className="pml-filter-label">Month</span>
            <select
              className="pml-select"
              value={trendMonth}
              onChange={(e) => setTrendMonth(parseInt(e.target.value, 10))}
            >
              {MONTH_NAMES.map((name, idx) => (
                <option key={idx} value={idx}>
                  {name}
                </option>
              ))}
            </select>

            <button className="pml-apply-btn" onClick={handleApplyTrend}>
              Apply
            </button>
          </div>

          {trendError && (
            <div className="pml-error-banner">
              Failed to load monthly trend data: {trendError}
            </div>
          )}

          {trendLoading ? (
            <div className="pml-empty-state">
              <div className="pml-spinner" aria-hidden="true" />
              <p>Loading trend data…</p>
            </div>
          ) : !trendDataRaw.length ? (
            <div className="pml-empty-state">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p>No data found for this timeframe</p>
              <span>Please select a new date range</span>
            </div>
          ) : (
            <>
              <div className="pml-chart-wrap">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={trendDataFull}
                    margin={{ top: 8, right: 16, left: 8, bottom: 32 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="day"
                      type="number"
                      domain={[1, trendDataFull.length]}
                      /* Every day 1–N explicitly listed - no skipping */
                      ticks={Array.from(
                        { length: trendDataFull.length },
                        (_, i) => i + 1,
                      )}
                      tick={{ fontSize: 9, fill: "#718096" }}
                      tickLine={{ stroke: "#e2e8f0" }}
                      axisLine={{ stroke: "#e2e8f0" }}
                      interval={0}
                      label={{
                        value: `Days of ${MONTH_NAMES_SHORT[appliedTrendMonth]}`,
                        position: "insideBottom",
                        offset: -18,
                        fontSize: 12,
                        fontWeight: 600,
                        fill: "#4a5568",
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#718096" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={fmtSqft}
                      width={48}
                      label={{
                        value: "SQFT Completed",
                        angle: -90,
                        position: "insideLeft",
                        offset: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        fill: "#4a5568",
                      }}
                    />
                    <Tooltip content={<TrendTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="sqft"
                      stroke="#cc0000"
                      strokeWidth={2}
                      connectNulls={true}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (payload.sqft === null)
                          return <g key={`dot-${payload.day}`} />;
                        return (
                          <circle
                            key={`dot-${payload.day}`}
                            cx={cx}
                            cy={cy}
                            r={3}
                            fill="#cc0000"
                            stroke="none"
                          />
                        );
                      }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {trendStats && (
                <div className="pml-chart-summary">
                  <div className="pml-chart-stat">
                    <span className="pml-chart-stat-label">Avg Daily SQFT</span>
                    <span className="pml-chart-stat-value">
                      {trendStats.avgSqft.toLocaleString()}
                    </span>
                  </div>
                  <div className="pml-chart-stat">
                    <span className="pml-chart-stat-label">Peak Day</span>
                    <span className="pml-chart-stat-value">
                      Day {trendStats.peakDay} -{" "}
                      {trendStats.peakSqft.toLocaleString()} sqft
                    </span>
                  </div>
                  <div className="pml-chart-stat">
                    <span className="pml-chart-stat-label">
                      Avg Active Personnel
                    </span>
                    <span className="pml-chart-stat-value">
                      {trendStats.avgPers}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────── */}

      <Modal
        isOpen={activeModal === "goals"}
        onClose={closeModal}
        title="Update Goals"
        size="large"
      >
        <EditGoals />
      </Modal>
      <Modal
        isOpen={activeModal === "ytdCompletion"}
        onClose={closeModal}
        title="YTD Module Completion"
        size="large"
      >
        <YtdModuleCompletion goals={goals} />
      </Modal>
      <Modal
        isOpen={activeModal === "completionVariance"}
        onClose={closeModal}
        title="Module Completion Variance"
        size="large"
      >
        <CompletionVariance />
      </Modal>
      <Modal
        isOpen={activeModal === "weeklySqft"}
        onClose={closeModal}
        title="Weekly SQFT Completed"
        size="large"
      >
        <WeeklySqftCompleted goals={goals} />
      </Modal>
      <Modal
        isOpen={activeModal === "firstPassRate"}
        onClose={closeModal}
        title="First Pass Rate (Utilities)"
        size="large"
      >
        <FirstPassRate goals={goals} />
      </Modal>
      <Modal
        isOpen={activeModal === "dailyModules"}
        onClose={closeModal}
        title="Daily Modules Completed"
        size="large"
      >
        <DailyModulesCompleted goals={goals} />
      </Modal>
    </div>
  );
};

export default ProductionMilestonesPage;
